import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Calendar, User, RefreshCw, Loader2, Inbox, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "boe_admin_token";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("de-CH", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const ContactsPanel = ({ onLogout }) => {
  const [messages, setMessages] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/admin/contacts`, { headers: authHeader() });
      setMessages(data);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Anfragen konnten nicht geladen werden.");
      setMessages([]);
    } finally {
      setRefreshing(false);
    }
  }, [onLogout]);

  const remove = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers: authHeader() });
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success("Anfrage gelöscht.");
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Löschen fehlgeschlagen.");
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <span className="section-eyebrow">Posteingang</span>
          <h1 className="mt-3 text-3xl md:text-4xl font-semibold text-cream tracking-tight">Alle Anfragen</h1>
        </div>
        <div className="flex items-center gap-4">
          {messages && (
            <span className="text-sm text-cream/60" data-testid="admin-count">
              {messages.length} {messages.length === 1 ? "Eintrag" : "Einträge"}
            </span>
          )}
          <button
            onClick={load}
            disabled={refreshing}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 text-sm text-cream/80 hover:text-gold-soft transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} /> Aktualisieren
          </button>
        </div>
      </div>

      {messages === null ? (
        <div className="flex items-center justify-center py-24 text-cream/60"><Loader2 size={28} className="animate-spin" /></div>
      ) : messages.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center text-cream/55" data-testid="admin-empty">
          <Inbox size={40} className="mb-4" style={{ color: "#E5C76B" }} />
          <p className="text-lg">Noch keine Anfragen.</p>
          <p className="text-sm mt-1">Sobald jemand das Kontaktformular ausfüllt, erscheint es hier.</p>
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2" data-testid="admin-list">
          {messages.map((m) => (
            <article key={m.id} data-testid={`admin-message-${m.id}`} className="rounded-2xl border border-gold/15 bg-white/[0.03] p-6 transition-colors hover:border-gold/35">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-2">
                  <User size={15} style={{ color: "#E5C76B" }} />
                  <span className="font-semibold text-cream">{m.name}</span>
                </div>
                <div className="flex items-center gap-3 whitespace-nowrap">
                  <span className="flex items-center gap-1.5 text-xs text-cream/50"><Calendar size={13} /> {formatDate(m.created_at)}</span>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid={`admin-delete-${m.id}`} disabled={deletingId === m.id} aria-label="Anfrage löschen" className="text-cream/40 hover:text-red-400 transition-colors disabled:opacity-50">
                        {deletingId === m.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-anthracite-soft border border-gold/20 text-cream">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-cream">Anfrage löschen?</AlertDialogTitle>
                        <AlertDialogDescription className="text-cream/60">
                          Möchtest du die Anfrage von <span className="text-cream/90 font-medium">{m.name}</span> wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-transparent border-white/20 text-cream hover:bg-white/10 hover:text-cream">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction data-testid={`admin-delete-confirm-${m.id}`} onClick={() => remove(m.id)} className="bg-red-600 text-white hover:bg-red-700">Löschen</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1.5 hover:underline" style={{ color: "#E5C76B" }}><Mail size={14} /> {m.email}</a>
                {m.location && <span className="inline-flex items-center gap-1.5 text-cream/70"><MapPin size={14} /> {m.location}</span>}
              </div>
              <p className="mt-4 text-cream/85 text-[15px] leading-relaxed whitespace-pre-wrap border-t border-cream/10 pt-4">{m.message}</p>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactsPanel;
